/**
 * Matches private-bucket app download URLs (`survey_files` or `idea_files`).
 */
const PRIVATE_BUCKET_FILE_DOWNLOAD_PATH =
  /\/(survey_files|idea_files)\/[^/]+\/download(?:[?#]|$)/i;

/**
 * Matches `.../survey_files/{id}/download` with optional `?query` or `#fragment`.
 * Used when enable_private_bucket_for_survey feature toggle is enabled.
 */
export function isSurveyFileDownloadUrl(url: string | undefined): boolean {
  if (!url) return false;
  return /\/survey_files\/[^/]+\/download(?:[?#]|$)/i.test(url);
}

/**
 * Matches `.../idea_files/{id}/download` with optional `?query` or `#fragment`.
 * Used when enable_private_bucket_for_ideas feature toggle is enabled.
 */
export function isIdeaFileDownloadUrl(url: string | undefined): boolean {
  if (!url) return false;
  return /\/idea_files\/[^/]+\/download(?:[?#]|$)/i.test(url);
}

export function isPrivateBucketFileDownloadUrl(
  url: string | undefined
): boolean {
  if (!url) return false;
  return PRIVATE_BUCKET_FILE_DOWNLOAD_PATH.test(url);
}

/**
 * Query-param name the private file download endpoints accept for the
 * bearer token. Confirm with backend before changing.
 */
const PRIVATE_FILE_AUTH_QUERY_PARAM = "access_token";

/**
 * Private bucket files use `/survey_files/{id}/download` or
 * `/idea_files/{id}/download`. Anonymous / unverified participants cannot send
 * the `x-api-token` header via an `<img>` or `<a>` request, so append the token
 * to the URL as a query param when available.
 */
export function appendAuthTokenToSurveyFileUrl(
  url: string | undefined,
  token: string | undefined | null
): string | undefined {
  if (!url || !token) return url;
  if (!isPrivateBucketFileDownloadUrl(url)) return url;
  if (new RegExp(`[?&]${PRIVATE_FILE_AUTH_QUERY_PARAM}=`).test(url)) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${PRIVATE_FILE_AUTH_QUERY_PARAM}=${encodeURIComponent(
    token
  )}`;
}

/** Presigned S3 URLs must never be submitted — only app download paths. */
export function isPresignedS3Url(url: string | undefined): boolean {
  if (!url) return false;
  return /amazonaws\.com/i.test(url) && /[?&]X-Amz-/i.test(url);
}

/** Remove display-only `access_token` before persisting file-url to the backend. */
export function stripAccessTokenFromPrivateFileUrl(
  url: string | undefined
): string | undefined {
  if (!url || !isPrivateBucketFileDownloadUrl(url)) return url;
  if (!url.includes(`${PRIVATE_FILE_AUTH_QUERY_PARAM}=`)) return url;

  const cleaned = url.replace(
    new RegExp(`([?&])${PRIVATE_FILE_AUTH_QUERY_PARAM}=[^&]*(&|$)`),
    (_, sep, rest) => (rest === "&" ? sep : "")
  );

  return cleaned.replace(/\?$/, "");
}

/**
 * App API download URLs (private bucket) cannot be proxied through imgix — use as-is.
 */
function isNonImgixAssetUrl(imageUrl: string): boolean {
  return isPrivateBucketFileDownloadUrl(imageUrl);
}

function normalizeImagePathForImgix(
  imageUrl: string,
  options?: { s3PathMode?: "legacy" | "host-only" }
): string {
  if (imageUrl.includes("imgix.net")) {
    return imageUrl.replace(/(.*)\.imgix\.net(\/)/, "");
  }

  let path = imageUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "");

  if (/\.amazonaws\.com\//i.test(path)) {
    if (options?.s3PathMode === "host-only") {
      // Private bucket: keep full S3 key (e.g. survey_files/{id}/file.jpg).
      path = path.replace(/^.*?\.amazonaws\.com\//i, "");
    } else {
      path = path.replace(/(s3-|s3\.)?(.*)\.amazonaws\.com(\/(.*?)\/)/, "");
    }
  }

  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) {
    path = path.substring(0, queryIndex);
  }

  return path;
}

function buildImgixUrlWithSource(
  imageUrl: string,
  imgixSource: string | undefined,
  height: number | "auto" = 1500,
  width: number | "auto" = 1500,
  fit = "clip",
  maxWidth = 2500,
  minWidth = 1500,
  s3PathMode: "legacy" | "host-only" = "legacy"
): string {
  if (!imageUrl || !imgixSource) {
    return imageUrl;
  }

  const imagePath = normalizeImagePathForImgix(imageUrl, { s3PathMode });

  return `https://${imgixSource}/${imagePath}?auto=format,compress&fit=${fit}&h=${height}&w=${width}&max-w=${maxWidth}&min-w=${minWidth}`;
}

export function buildImgixUrl(
  imageUrl: string,
  height: number | "auto" = 1500,
  width: number | "auto" = 1500,
  fit = "clip",
  maxWidth = 2500,
  minWidth = 1500
) {
  if (!imageUrl || !process.env.IMGIX_SOURCE) {
    return imageUrl;
  }

  if (isNonImgixAssetUrl(imageUrl)) {
    return imageUrl;
  }

  return buildImgixUrlWithSource(
    imageUrl,
    process.env.IMGIX_SOURCE,
    height,
    width,
    fit,
    maxWidth,
    minWidth
  );
}

/**
 * Imgix source for private-bucket media (presigned S3 URLs from survey/ideas uploads).
 */
export function buildPrivateImgixUrl(
  imageUrl: string,
  height: number | "auto" = 1500,
  width: number | "auto" = 1500,
  fit = "clip",
  maxWidth = 2500,
  minWidth = 1500
) {
  if (!imageUrl || !process.env.IMGIX_PRIVATE_SOURCE) {
    return imageUrl;
  }

  const path = imageUrl.split("?")[0];
  if (/\.(pdf|doc|docx|zip|xls|xlsx|ppt|pptx|rtf|mp3|mp4|wav)$/i.test(path)) {
    return imageUrl;
  }

  return buildImgixUrlWithSource(
    imageUrl,
    process.env.IMGIX_PRIVATE_SOURCE,
    height,
    width,
    fit,
    maxWidth,
    minWidth,
    "host-only"
  );
}
